/**
 * Domain Branding Utility
 * 
 * Provides functions to detect current domain and get domain-specific branding config
 */

import domainBrandingConfig from "../config/domainBrandingConfig";

/**
 * Get the current domain from window.location
 * @returns {string} Current domain (e.g., "hamro.ptmantra.com")
 */
export const getCurrentDomain = () => {
  if (typeof window === "undefined") {
    return null;
  }
  return window.location.hostname;
};

/**
 * Get domain branding config based on current domain
 * @param {string} domain - Optional domain to check (defaults to current domain)
 * @returns {Object|null} Domain branding config or null if not found
 */
export const getDomainBrandingConfig = (domain = null) => {
  const currentDomain = domain || getCurrentDomain();
  
  if (!currentDomain) {
    return null;
  }

  // Check for exact domain match
  // Both staging and production domains should be explicitly listed in domainBrandingConfig
  // e.g., "hamro.ptmantra.com" and "stage-hh.ptmantra.com"
  if (domainBrandingConfig[currentDomain]) {
    return domainBrandingConfig[currentDomain];
  }

  // Pattern matching disabled - only exact domain matches are used
  // This prevents "stage.ptmantra.com" from matching "stage-hh.ptmantra.com"
  // Each domain must be explicitly configured in domainBrandingConfig
  return null;
};

/**
 * Check if current domain has domain-based branding configured
 * @returns {boolean} True if domain branding is configured
 */
export const hasDomainBranding = () => {
  return getDomainBrandingConfig() !== null;
};

/**
 * Merge domain config with API config
 * Domain config takes priority over API config
 * @param {Object} domainConfig - Domain branding config
 * @param {Object} apiConfig - API branding config
 * @returns {Object} Merged config with domain config taking priority
 */
export const mergeBrandingConfig = (domainConfig, apiConfig) => {
  if (!domainConfig) {
    return apiConfig || {};
  }

  if (!apiConfig) {
    return domainConfig;
  }

  // Merge configs with domain config taking priority
  // Only use domain logo if it's truthy (not undefined, null, or empty string)
  const mergedLogo = (domainConfig.logo && domainConfig.logo !== "") 
    ? domainConfig.logo 
    : (apiConfig.logo && apiConfig.logo !== "" ? apiConfig.logo : undefined);
  
  const mergedLogoBgColor = domainConfig.logoBackgroundColor || apiConfig.logoBackgroundColor;
  
  return {
    logo: mergedLogo,
    logoBackgroundColor: mergedLogoBgColor,
    skin: {
      BrandColor: domainConfig.skin?.BrandColor || apiConfig.skin?.BrandColor,
      favicon: domainConfig.skin?.favicon || apiConfig.skin?.favicon,
    },
    // Preserve other API config properties
    ...apiConfig,
    // Override with merged values (domain takes priority)
    logo: mergedLogo,
    logoBackgroundColor: mergedLogoBgColor,
    skin: {
      ...apiConfig.skin,
      ...domainConfig.skin,
      BrandColor: domainConfig.skin?.BrandColor || apiConfig.skin?.BrandColor,
      favicon: domainConfig.skin?.favicon || apiConfig.skin?.favicon,
    },
  };
};

